/* ---------------------------------------------------------------------------
   ab-buy-box.js

   Behaviour for the purchase column. The static Figma frames imply all of it:
   two option cards that behave as a radio group, two rows that open and close,
   a dropdown that is a custom listbox rather than a native select, and a
   subtotal and button label that follow whatever is chosen.

   Everything it needs is read off data attributes written by the Liquid, so
   this file carries no product, price or variant of its own.

   On add it posts the lines itself. The subscription option puts the recurring
   item in as a second line with the selling plan on it, which a plain product
   form cannot express.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  function money(cents) {
    var n = (cents || 0) / 100;
    var s = n.toLocaleString('en-US', {
      minimumFractionDigits: n % 1 ? 2 : 0,
      maximumFractionDigits: 2
    });
    return '$' + s;
  }

  /* The buy column sits inside the theme's tall `position: sticky` panel. Once
     the page is scrolled past that panel the browser holds its BOTTOM against
     the viewport, so anything that grows inside it extends UPWARD rather than
     downward. Measured on the dev theme: opening a row added 46.9px to the
     panel and dropped its document top by exactly 46.9px, carrying the row the
     shopper had just clicked 46.9px off the top of the screen, with no scroll
     event at all. Browsers leave sticky subtrees out of their own scroll
     anchoring, so this holds the clicked element still by hand, scrolling the
     same amount the other way for as long as the box is still changing size.
     Where the panel is not stuck the drift is zero and this does nothing. */
  var releaseAnchor = null;

  function anchor(el, ms) {
    if (!el || !el.getBoundingClientRect) return function () {};
    /* two controls can change on one click, and two corrections would fight */
    if (releaseAnchor) releaseAnchor();
    var start = el.getBoundingClientRect().top;
    var until = Date.now() + (ms || 400);
    var live = true;
    (function step() {
      if (!live) return;
      var drift = el.getBoundingClientRect().top - start;
      /* the theme sets html{scroll-behavior:smooth} theme wide, which would turn
         each correction into its own animation and make this wobble */
      if (drift) window.scrollBy({ top: drift, left: 0, behavior: 'instant' });
      if (Date.now() < until) requestAnimationFrame(step);
      else live = false;
    })();
    releaseAnchor = function () { live = false; };
    return releaseAnchor;
  }

  function init(root) {
    if (!root || root.__abxReady) return;
    root.__abxReady = true;

    var basePrice = parseInt(root.getAttribute('data-base-price'), 10) || 0;
    var mainVariant = parseInt(root.getAttribute('data-main-variant'), 10) || 0;
    var subVariant = parseInt(root.getAttribute('data-sub-variant'), 10) || 0;
    var subPlan = parseInt(root.getAttribute('data-sub-plan'), 10) || 0;

    /* af236, NeXcia X product page only, picked out by the template class Shopify
       puts on <body>. There the box opens with the 2 year plan already chosen and the
       figure on screen stays the product price whatever plan is picked. The plan is
       still added to the cart, so the cart is where the real total appears. Every
       other page that renders this box, the landing page included, is untouched. */
    var isNexciaX = !!(document.body && document.body.classList.contains('template-suffix-nexcia-x'));
    var preselectYears = isNexciaX ? 2 : 0;
    var totalExcludesProt = isNexciaX;

    var totalEl = root.querySelector('[data-abx-total]');
    var ctaPriceEl = root.querySelector('[data-abx-cta-price]');
    var addBtn = root.querySelector('[data-abx-add]');
    var errEl = root.querySelector('[data-abx-err]');

    /* ---- protection dropdown ------------------------------------------- */
    var sel = root.querySelector('[data-abx-sel]');
    var selBtn = sel && sel.querySelector('[data-abx-sel-btn]');
    var selVal = sel && sel.querySelector('[data-abx-sel-val]');
    var selOpts = sel ? Array.prototype.slice.call(sel.querySelectorAll('[data-abx-sel-opt]')) : [];
    var protCents = 0;
    var protVariant = 0;

    function chooseProt(opt) {
      if (!opt) return;
      protCents = parseInt(opt.getAttribute('data-price'), 10) || 0;
      protVariant = parseInt(opt.getAttribute('data-variant'), 10) || 0;
      if (selVal) selVal.textContent = opt.textContent;

      /* hand the choice back to SureBright so the quote and the policy stay theirs */
      if (opt.__sbCard) {
        if (!opt.__sbCard.classList.contains('selected-offer')) opt.__sbCard.click();
      } else if (opt.getAttribute('data-sb') === 'none') {
        sbClear();
      }
      selOpts.forEach(function (o) {
        o.setAttribute('aria-selected', o === opt ? 'true' : 'false');
      });
      closeSel();
      paint();
    }

    function openSel() {
      if (!sel || sel.classList.contains('is-open')) return;
      anchor(selBtn, 120);
      sel.classList.add('is-open');
      if (selBtn) selBtn.setAttribute('aria-expanded', 'true');
    }
    function closeSel() {
      if (!sel || !sel.classList.contains('is-open')) return;
      anchor(selBtn, 120);
      sel.classList.remove('is-open');
      if (selBtn) selBtn.setAttribute('aria-expanded', 'false');
    }

    if (selBtn) {
      selBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (sel.classList.contains('is-open')) closeSel(); else openSel();
      });
    }
    selOpts.forEach(function (o) {
      o.addEventListener('click', function () { chooseProt(o); });
    });
    document.addEventListener('click', function (e) {
      if (sel && !sel.contains(e.target)) closeSel();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeSel();
    });

    /* The Liquid rows carry a price but no policy behind them: they are only a
       placeholder until SureBright answers, and sbBuildOptions replaces the whole
       list when it does. Offered as they are, a shopper can pick "3 years" in the
       first seconds, watch the button say $1,249, and land on a $1,000 cart with
       no warranty. So they are taken out now and come back from SureBright. */
    sbStrip(false);
    /* Until SureBright answers, the only row left is the free one, and a
       dropdown whose single choice is "No additional protection" reads as
       broken. On a cold load their quote can take seven seconds or more, which
       is a long time to show that. So the control stays out of the page until
       there is a real choice to make, and sbBuildOptions brings it in. A
       merchant row backed by a real variant is a real choice, so a control
       configured that way still appears at once. */
    if (selOpts.length < 2) sbShowProt(false);

    function sbShowProt(on) {
      var protEl = root.querySelector('.abx-prot');
      var lblEl = root.querySelector('.abx-prot__label');
      if (protEl) protEl.style.display = on ? '' : 'none';
      if (lblEl) lblEl.style.display = on ? '' : 'none';
    }
    /* ---- SureBright ------------------------------------------------------
       The warranty prices are not ours to invent: SureBright quotes them per
       product, so the three options in the Figma dropdown have to be read off
       their widget rather than typed into a setting. Their own tile is hidden
       and this control drives it, so the quote, the policy ids and the adding
       stay theirs.

       Their widget also arrives pre-selected on the 2 year plan, which is why
       carts have been coming out higher than the price on the button. The
       Figma opens on "No additional protection", so that is what we set. */
    var SB_TILE = '.offering-wrapper .offering-tile';
    var sbSynced = false;

    function sbCards() {
      var tile = document.querySelector(SB_TILE);
      if (!tile) return [];
      return Array.prototype.slice.call(tile.querySelectorAll('.offeringCard'));
    }

    function sbRead() {
      return sbCards().map(function (card) {
        var years = card.querySelector('.coverageYears');
        var price = card.querySelector('.policyPrice');
        var label = years ? years.textContent.replace(/\s+/g, ' ').trim() : '';
        var priceText = price ? price.textContent.trim() : '';
        var cents = Math.round(parseFloat(priceText.replace(/[^0-9.]/g, '')) * 100) || 0;
        return { card: card, label: label, priceText: priceText, cents: cents };
      }).filter(function (o) { return o.label; });
    }

    function sbClear() {
      var tile = document.querySelector(SB_TILE);
      if (!tile) return;
      var sel = tile.querySelector('.offeringCard.selected-offer');
      if (!sel) return;
      var remove = sel.querySelector('.sb-pdp-remove');
      if (remove) remove.click();
    }

    function sbBuildOptions() {
      var offers = sbRead();
      if (!offers.length || !sel) return false;

      var list = sel.querySelector('[data-abx-sel-list]');
      if (!list) return false;

      /* the control is held back until there is a real choice; this is that moment */
      sbShowProt(true);

      /* keep the merchant's own first row, which is the "none" option */
      var none = list.querySelector('[data-abx-sel-opt]');
      list.innerHTML = '';
      if (none) {
        none.setAttribute('data-sb', 'none');
        list.appendChild(none);
      }

      offers.forEach(function (o) {
        var li = document.createElement('li');
        li.className = 'abx-sel__opt';
        li.setAttribute('role', 'option');
        li.setAttribute('data-abx-sel-opt', '');
        li.setAttribute('data-price', String(o.cents));
        li.setAttribute('data-variant', '0');
        li.textContent = o.label + ' \u2014 ' + o.priceText;
        li.__sbCard = o.card;
        /* their label reads "02 Year" while the quote says "2" */
        li.__sbYears = parseInt((o.label.match(/(\d+)/) || [])[1], 10) || 0;
        list.appendChild(li);
      });

      selOpts = Array.prototype.slice.call(list.querySelectorAll('[data-abx-sel-opt]'));
      selOpts.forEach(function (o) {
        o.addEventListener('click', function () { chooseProt(o); });
      });

      /* their tile is replaced by this control, so it should not also be drawn */
      var tile = document.querySelector(SB_TILE);
      if (tile) tile.style.display = 'none';

      sbClear();
      chooseProt(sbDefaultOpt());
      return true;
    }

    /* selOpts[0] is the merchant's own "no additional protection" row. When the page
       asks for a plan up front, take the row carrying that number of years, and fall
       back to the none row if SureBright did not quote it. */
    function sbDefaultOpt() {
      if (!preselectYears) return selOpts[0];
      for (var i = 0; i < selOpts.length; i++) {
        if (selOpts[i].__sbYears === preselectYears) return selOpts[i];
      }
      return selOpts[0];
    }

    function sbWatch() {
      if (sbSynced) return;
      if (sbBuildOptions()) { sbSynced = true; return; }
    }

    /* If SureBright never quotes this product there is no policy behind the
       priced rows, so offering them would promise a warranty that cannot be
       added. Only the "no protection" row survives. */
    function sbStrip(hideWhenBare) {
      if (sbSynced || !sel) return;
      var list = sel.querySelector('[data-abx-sel-list]');
      if (!list) return;
      var rows = Array.prototype.slice.call(list.querySelectorAll('[data-abx-sel-opt]'));
      var keep = rows.filter(function (o) {
        return parseInt(o.getAttribute('data-price'), 10) === 0 ||
               parseInt(o.getAttribute('data-variant'), 10) > 0;
      });
      if (keep.length !== rows.length) {
        rows.forEach(function (o) { if (keep.indexOf(o) === -1) o.remove(); });
        selOpts = keep;
        if (selOpts.length) chooseProt(selOpts[0]);
      }
      /* Only the 8 second timeout hides the control. At init the rows are taken
         out because SureBright has not answered YET, and it still may, so the
         control stays on screen showing "No additional protection". */
      if (hideWhenBare && selOpts.length < 2) sbShowProt(false);
    }

    function sbGiveUp() { sbStrip(true); }

    /* True while SureBright has not yet asked for a quote, which means it is
       still loading rather than finished with nothing to say. */
    function sbQuotePending() {
      try {
        var rs = performance.getEntriesByType('resource');
        for (var i = 0; i < rs.length; i++) {
          if (rs[i].name.indexOf('surebright') !== -1 && /quote/i.test(rs[i].name)) return false;
        }
        return true;
      } catch (e) { return false; }
    }

    if (sel) {
      sbWatch();
      if (!sbSynced) {
        var mo = new MutationObserver(function () { sbWatch(); if (sbSynced) mo.disconnect(); });
        mo.observe(document.body, { childList: true, subtree: true });
        /* Give up quietly rather than watching the page for ever, but do not give
           up while SureBright is demonstrably still working. Their SDK is over
           3 MB, so on a first visit on a slow connection the quote can land well
           after 8 seconds, and hiding the control before it arrives would cost
           the shopper the warranty. */
        var waited = 0;
        var tick = setInterval(function () {
          waited += 1000;
          if (sbSynced) { clearInterval(tick); mo.disconnect(); return; }
          if (waited < 8000) return;
          if (waited < 25000 && sbQuotePending()) return;
          clearInterval(tick);
          mo.disconnect();
          sbGiveUp();
        }, 1000);
      }
    }

    if (selOpts.length && !sbSynced) chooseProt(selOpts[0]);

    /* ---- the two purchase options -------------------------------------- */
    var opts = Array.prototype.slice.call(root.querySelectorAll('[data-abx-opt]'));

    function selected() {
      var on = root.querySelector('.abx-opt.is-on');
      return on ? on.getAttribute('data-abx-opt') : 'sub';
    }

    opts.forEach(function (card) {
      card.addEventListener('click', function () {
        opts.forEach(function (c) { c.classList.toggle('is-on', c === card); });
        var input = card.querySelector('.abx-opt__input');
        if (input) input.checked = true;
        paint();
      });
    });

    /* ---- open and close rows ------------------------------------------- */
    Array.prototype.forEach.call(root.querySelectorAll('[data-abx-row]'), function (row) {
      var btn = row.querySelector('[data-abx-row-btn]');
      var panel = row.querySelector('[data-abx-row-a]');
      if (!btn || !panel) return;

      /* the open and shut animation is grid-template-rows in the stylesheet, so
         nothing here has to measure a height that fonts or images may change.
         'is-settled' lifts the clipping once the row has finished opening, which
         is what lets the protection dropdown hang below its trigger. */
      function settle() { if (row.classList.contains('is-open')) row.classList.add('is-settled'); }

      ['transitionend', 'transitioncancel'].forEach(function (ev) {
        panel.addEventListener(ev, function (e) {
          if (e.propertyName === 'grid-template-rows') settle();
        });
      });

      btn.addEventListener('click', function () {
        /* 220ms is the stylesheet's transition; hold a little past it */
        anchor(btn, 400);
        var open = row.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (!open) row.classList.remove('is-settled');
      });

      /* a row that arrives open never fires a transition */
      if (row.classList.contains('is-open')) settle();
    });

    /* ---- price ---------------------------------------------------------- */
    function total() {
      return totalExcludesProt ? basePrice : basePrice + protCents;
    }

    function paint() {
      var t = money(total());
      if (totalEl) totalEl.textContent = t;
      if (ctaPriceEl) ctaPriceEl.textContent = t;
    }

    /* ---- add to cart ---------------------------------------------------- */
    function showError(msg) {
      if (!errEl) return;
      errEl.textContent = msg;
      errEl.hidden = !msg;
    }

    function postJson(url, data) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
    }

    /* SureBright adds its policy by hooking the theme's add to cart. This box
       posts the cart itself, so that hook never runs and the warranty was
       being left behind. Their quote sits in localStorage under
       SB_QUOTE_DETAILS, keyed productId#variantId#country, and carries the
       variant and the quote ids for each term, which is everything the line
       item needs. A bundle product quotes its component instead of itself,
       which is why componentQuoteList comes first. */
    function sbLineItem() {
      var current = root.querySelector('[data-abx-sel-opt][aria-selected="true"]');
      var years = current ? current.__sbYears : 0;
      if (!years) return null;

      var store;
      try { store = JSON.parse(localStorage.getItem('SB_QUOTE_DETAILS')); } catch (e) { return null; }
      if (!store || !store.value) return null;

      var quote = null;
      Object.keys(store.value).forEach(function (k) {
        if (k.indexOf(String(mainVariant)) !== -1) quote = store.value[k];
      });
      if (!quote) {
        var first = Object.keys(store.value)[0];
        quote = first ? store.value[first] : null;
      }
      if (!quote) return null;

      var comp = quote.componentQuoteList && quote.componentQuoteList[0];
      var policies = (comp && comp.PolicyData) || quote.PolicyData;
      if (!policies) return null;

      var policy = null;
      policies.forEach(function (pd) {
        if (parseInt(pd.coverageYears, 10) === years) policy = pd;
      });
      if (!policy || !policy.variantId) return null;

      var props = {
        Product: comp
          ? quote.productTitle + ' - ' + comp.componentProductTitle
          : quote.productTitle,
        Term: years + ' Year',
        _sbPriceVersionId: policy.priceVersionId || '001',
        _sbQuoteId: policy.quoteId,
        _sbQuoteItemId: policy.quoteItemId,
        _variantId: String(comp ? comp.componentVariantId : quote.storeVariantId)
      };
      if (comp) {
        props._bundleComponentCount = String(quote.componentQuoteList.length);
        props._bundleParentProductId = String(quote.storeProductId);
        props._bundleParentVariantId = String(quote.storeVariantId);
        props._componentQuantity = String(comp.quantity || 1);
        props._isBundleComponentPolicy = 'true';
      }

      return { id: policy.variantId, quantity: 1, properties: props };
    }

    function payload() {
      var items = [];
      /* the cart lists newest first, so anything that should appear BELOW the
         bundle goes in first */
      var warranty = sbLineItem();
      if (warranty) {
        items.push(warranty);
      } else if (protVariant) {
        items.push({ id: protVariant, quantity: 1 });
      } else if (protCents > 0) {
        /* a priced protection is selected but nothing can carry it: the quote is
           missing or the row is one the page drew before SureBright answered.
           Posting here would charge the shopper less than the button promised
           and hand them no policy, so the add stops instead. */
        return null;
      }
      if (selected() === 'sub' && subVariant) {
        var line = { id: subVariant, quantity: 1 };
        if (subPlan) line.selling_plan = subPlan;
        /* founders-autoadd only takes the X Box back out of a cart when the line
           carries the flag it writes itself. Without it, a shopper who removes
           the bundle on the cart page is left holding the subscription on its
           own, and its first month is free, so the cart totals $0. */
        line.properties = { _founders_auto: '1' };
        items.push(line);
      }
      if (mainVariant) {
        var main = { id: mainVariant, quantity: 1 };
        /* founders-autoadd puts the X Box on any cart holding the Founders
           bundle, which is right for every other way in but wrong here: Lite
           is the choice NOT to have it. This marks the line so that script
           leaves the cart alone. */
        if (selected() !== 'sub') {
          main.properties = { _founders_lite: '1' };
        }
        items.push(main);
      }
      return { items: items };
    }

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        if (addBtn.disabled) return;
        addBtn.disabled = true;
        showError('');

        var body = payload();
        if (!body) {
          addBtn.disabled = false;
          showError('That protection plan is still loading. Give it a moment, or choose "No additional protection".');
          return;
        }

        fetch('/cart/clear.js', { method: 'POST' })
          .then(function () { return postJson('/cart/add.js', body); })
          .then(function (res) {
            return res.json().then(function (body) {
              if (!res.ok) {
                throw new Error(body.description || body.message || ('HTTP ' + res.status));
              }
              return body;
            });
          })
          /* nothing is applied here; this only sweeps off a code the shopper
             may have picked up earlier in the session */
          .then(function () { return postJson('/cart/update.js', { discount: '' }); })
          .then(function () { window.location.href = '/cart'; })
          .catch(function (err) {
            addBtn.disabled = false;
            showError('Could not add to cart: ' + err.message);
          });
      });
    }

    paint();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-abx-buy]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* the section can be re-rendered by the theme editor */
  document.addEventListener('shopify:section:load', boot);
})();
